import axios, { AxiosError } from 'axios';
import type { ApiResponse, ApiError } from '@shared/types';

const API_URL = import.meta.env.VITE_API_URL || '';

export const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiResponse<unknown>>) => {
    if (error.response?.status === 401) {
      // Unauthorized - redirect to login, but only if not already on login page
      // and not checking auth status (which expects 401 when not logged in)
      const isAuthCheck = error.config?.url?.includes('/auth/me');
      const isOnLoginPage = window.location.pathname === '/login';

      if (!isAuthCheck && !isOnLoginPage) {
        window.location.href = '/login';
      }
    }

    const apiError: ApiError = error.response?.data?.error || {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred',
    };

    return Promise.reject(apiError);
  }
);

// Helper functions for common API operations
export async function fetchData<T>(endpoint: string): Promise<T> {
  const response = await api.get<ApiResponse<T>>(endpoint);
  if (!response.data.success || !response.data.data) {
    throw response.data.error || { code: 'NO_DATA', message: 'No data returned' };
  }
  return response.data.data;
}

export async function postData<T, R>(endpoint: string, data: T): Promise<R> {
  const response = await api.post<ApiResponse<R>>(endpoint, data);
  if (!response.data.success || !response.data.data) {
    throw response.data.error || { code: 'POST_FAILED', message: 'Operation failed' };
  }
  return response.data.data;
}

export async function updateData<T, R>(endpoint: string, data: T): Promise<R> {
  const response = await api.put<ApiResponse<R>>(endpoint, data);
  if (!response.data.success || !response.data.data) {
    throw response.data.error || { code: 'UPDATE_FAILED', message: 'Update failed' };
  }
  return response.data.data;
}

export async function deleteData(endpoint: string): Promise<void> {
  const response = await api.delete<ApiResponse<null>>(endpoint);
  if (!response.data.success) {
    throw response.data.error || { code: 'DELETE_FAILED', message: 'Delete failed' };
  }
}
